import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    Mic,
    StopCircle,
    Sparkles,
    RotateCcw,
    ArrowLeft,
    Volume2,
    Turtle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ttsLanguageOf, voiceOf } from "@/lib/lang";
import { useDashboard } from "@/contexts/DashboardContext";

export default function Pronunciation() {
    const [challenge, setChallenge] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("idle"); // idle | recording | recorded | analyzing
    const [blob, setBlob] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const mrRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const bootstrapped = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (bootstrapped.current) return;
        bootstrapped.current = true;
        (async () => {
            try {
                const [{ data: chData }, { data: prof }] = await Promise.all([
                    api.post("/commands/challenge"),
                    api.get("/profile/me"),
                ]);
                setChallenge(chData.challenge);
                setProfile(prof.profile);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Intentional mount-only fetch — bootstrapped ref guards StrictMode
        // double-invoke; nothing changes across renders.
    }, []);

    async function start() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.onstop = () => {
                const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                setBlob(b);
                setBlobUrl(URL.createObjectURL(b));
                stream.getTracks().forEach((t) => t.stop());
                setStatus("recorded");
            };
            rec.start();
            mrRef.current = rec;
            setStatus("recording");
            setAnalysis(null);
        } catch {
            toast.error("Impossible d'accéder au micro. Autorisez-le dans le navigateur.");
        }
    }
    function stop() {
        if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop();
    }

    async function analyze() {
        if (!blob || !challenge) return;
        setStatus("analyzing");
        try {
            const form = new FormData();
            form.append("audio", blob, "recording.webm");
            form.append("expected_sentence", challenge.target_sentence);
            form.append("language", ttsLanguageOf(challenge.language_fr));
            const { data } = await api.post("/poc/analyze-pronunciation", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAnalysis(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setStatus("recorded");
        }
    }

    async function playTts(speed) {
        if (!challenge) return;
        try {
            const { data } = await api.post("/poc/tts", {
                text: challenge.target_sentence,
                language: ttsLanguageOf(challenge.language_fr),
                speed,
                voice: voiceOf(profile),
            });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="pron-loading">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="pronunciation-page">
            <button
                onClick={() => navigate("/dashboard")}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="pron-back"
            >
                <ArrowLeft className="w-4 h-4" /> Tableau de bord
            </button>

            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Prononciation ciblée</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
                {challenge?.title_fr || "Une phrase à travailler"}
            </h1>
            <p className="mt-3 text-sm text-inkSoft max-w-2xl" data-testid="pron-challenge-blurb">
                {challenge?.why_fr}
            </p>

            <div className="mt-8 bg-white border border-lexiBorder rounded-2xl p-6 md:p-8" data-testid="pron-card">
                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Phrase du jour</p>
                <p className="mt-3 font-serif text-2xl md:text-3xl leading-snug" data-testid="pron-target-sentence">
                    « {challenge?.target_sentence} »
                </p>
                <p className="mt-2 text-sm text-inkSoft">{challenge?.target_sentence_translation_fr}</p>

                <div className="mt-4 flex gap-2">
                    <button
                        onClick={() => playTts("natural")}
                        data-testid="pron-tts-natural"
                        className="inline-flex items-center gap-1.5 text-xs bg-ink text-white hover:bg-emerald rounded-full px-3 py-1.5"
                    >
                        <Volume2 className="w-3.5 h-3.5" /> Écouter
                    </button>
                    <button
                        onClick={() => playTts("slow")}
                        data-testid="pron-tts-slow"
                        className="inline-flex items-center gap-1.5 text-xs bg-ink text-white hover:bg-emerald rounded-full px-3 py-1.5"
                    >
                        <Turtle className="w-3.5 h-3.5" /> Écouter lentement
                    </button>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    {status !== "recording" && !blobUrl && (
                        <button
                            onClick={start}
                            data-testid="pron-start-btn"
                            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-4 py-2 text-sm active:scale-[0.98] transition-transform"
                        >
                            <Mic className="w-4 h-4" /> S&apos;enregistrer
                        </button>
                    )}
                    {status === "recording" && (
                        <button
                            onClick={stop}
                            data-testid="pron-stop-btn"
                            className="inline-flex items-center gap-2 bg-ink text-white rounded-full px-4 py-2 text-sm"
                        >
                            <StopCircle className="w-4 h-4 animate-pulse" /> Arrêter
                        </button>
                    )}
                    {blobUrl && (
                        <>
                            <audio src={blobUrl} controls className="h-9" data-testid="pron-playback" />
                            <button
                                onClick={() => {
                                    setBlob(null);
                                    setBlobUrl(null);
                                    setAnalysis(null);
                                    setStatus("idle");
                                }}
                                data-testid="pron-reset-btn"
                                className="text-xs text-inkSoft hover:text-ink inline-flex items-center gap-1"
                            >
                                <RotateCcw className="w-3 h-3" /> Recommencer
                            </button>
                            <button
                                onClick={analyze}
                                disabled={status === "analyzing"}
                                data-testid="pron-analyze-btn"
                                className="inline-flex items-center gap-2 bg-emerald hover:bg-emeraldSoft text-white rounded-full px-4 py-2 text-sm disabled:opacity-60 active:scale-[0.98] transition-transform"
                            >
                                {status === "analyzing" ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                Analyser
                            </button>
                        </>
                    )}
                </div>

                {analysis && analysis.status === "error" && (
                    <p className="mt-4 text-sm text-terracotta" data-testid="pron-analysis-error">
                        {analysis.error_reason}
                    </p>
                )}

                {analysis && analysis.status !== "error" && (
                    <div className="mt-6" data-testid="pron-analysis-ok">
                        <p className="text-xs text-inkSoft">
                            Entendu : <span className="font-mono text-ink">{analysis.heard_transcription}</span>
                        </p>
                        <p className="mt-1 text-sm">
                            Score global :{" "}
                            <span className="font-serif text-2xl text-emerald">{analysis.overall_score}</span>
                            /100
                        </p>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                            {["sons", "accent_tonique", "rythme", "intonation", "fluidite"].map((axis) => (
                                <div key={axis} className="bg-paperAlt border border-lexiBorder rounded-xl p-3">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-inkSoft">
                                        {axis.replace("_", " ")}
                                    </div>
                                    <div className="font-serif text-lg text-ink">
                                        {analysis.axes?.[axis]?.score ?? 0}
                                    </div>
                                    <div className="text-[10px] text-inkSoft italic mt-1">
                                        {analysis.axes?.[axis]?.comment}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-sm italic">{analysis.targeted_tip}</p>
                    </div>
                )}
            </div>

            <p className="mt-6 text-xs text-inkSoft">
                Nouveau défi disponible demain (à chaque changement de jour calendaire).
            </p>
        </div>
    );
}
